import type { WithRetryOptions } from '@/utils/withRetry';
import { GraphRequestError } from './graph-request-error';

/**
 * `services/graph/` holds the Microsoft Graph TRANSPORT primitives — error shape, retry policy,
 * chunked upload — that every Graph workload in this client shares, regardless of which resource it
 * talks to. It is a peer of `services/sharepoint-drive/` (and now `graph-mail-service.ts`), not a
 * layer above them.
 *
 * The split rule: anything that would read identically for files, mail, calendar or users belongs
 * here; anything encoding one resource's own semantics stays with its service (e.g.
 * `sharepoint-drive/copy-monitor-error.ts`, which exists purely because a drive copy is the one
 * asynchronous, monitor-observed operation in that API).
 *
 * These three were previously module-private consts inside sharepoint-drive-service.ts. Nothing about
 * them was ever drive-specific; lifting them out is what keeps a mail service from importing sideways
 * into the drive service, a wrong-way dependency every later Graph feature would have inherited.
 */
export const RETRYABLE_STATUSES = new Set([429, 503]);

/**
 * Retry only on Graph throttling/transient-unavailable (429/503) or a genuine network failure
 * (fetch rejects with a TypeError). A GraphRequestError with any other status (404/403/409/500…)
 * is fatal and must fail fast — no blind retry of non-idempotent-unsafe outcomes.
 *
 * An aborted fetch rejects with a DOMException named 'AbortError', which is neither of those, so an
 * abort is never retried. That is load-bearing rather than incidental: the mail-draft orchestrator's
 * cancel path depends on an aborted download/attach failing immediately rather than sitting out four
 * attempts, and graph-retry.test.ts pins it.
 */
export const isRetryableGraphError = (error: unknown): boolean =>
  error instanceof GraphRequestError
    ? RETRYABLE_STATUSES.has(error.status)
    : error instanceof TypeError;

export const GRAPH_RETRY_OPTIONS: WithRetryOptions = {
  maxRetries: 3,
  baseDelayMs: 500,
  maxDelayMs: 8000,
  isRetryable: isRetryableGraphError,
  getRetryDelayMs: (error) =>
    error instanceof GraphRequestError ? error.retryAfterMs : undefined,
};
